import React, { useState } from "react";
import { motion } from "framer-motion";
import { Target, Users, ArrowRightLeft } from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { EmployeePanel } from "@/components/EmployeePanel";
import { useStore } from "@/lib/store";
import { DEPARTMENTS } from "@/data/seed";

export default function Departments() {
  const { state } = useStore();
  const [panelId, setPanelId] = useState(null);
  const depts = DEPARTMENTS.filter((d) => d.managerId);

  return (
    <div>
      {panelId && <EmployeePanel employeeId={panelId} onClose={() => setPanelId(null)} />}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-white sm:text-4xl">Departments</h1>
        <p className="mt-1 text-sm text-slate-400">Teams of AI associates working toward shared objectives</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {depts.map((d, i) => {
          const members = state.employees.filter((e) => e.department === d.id);
          const manager = state.employees.find((e) => e.id === d.managerId);
          const others = depts.filter((x) => x.id !== d.id).slice(0, 2);
          return (
            <motion.div key={d.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="glass rounded-2xl p-5" data-testid={`department-${d.id}`} style={{ borderTop: `2px solid ${d.color}` }}>
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-display text-xl font-semibold text-white">{d.name}</h2>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-400"><Users size={13} /> {members.length} active associates</div>
                </div>
                {manager && (
                  <button onClick={() => setPanelId(manager.id)} className="flex items-center gap-2 rounded-xl border border-white/10 px-2 py-1.5 hover:border-[#00E5FF]/40" data-testid={`dept-manager-${d.id}`}>
                    <EmployeeAvatar employee={manager} size={32} showStatus={false} />
                    <div className="text-left"><div className="text-[11px] text-slate-500">Manager</div><div className="text-xs text-white">{manager.name}</div></div>
                  </button>
                )}
              </div>

              <div className="mt-3 flex items-start gap-2 rounded-xl border border-white/8 bg-[#030A16]/40 p-3 text-sm text-slate-300"><Target size={15} className="mt-0.5 shrink-0" style={{ color: d.color }} /> {d.objective}</div>

              <div className="mt-3">
                <div className="mb-1 flex justify-between text-[11px] font-mono text-slate-500"><span>Workload</span><span>{d.workload}%</span></div>
                <div className="h-1.5 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full" style={{ width: `${d.workload}%`, background: d.color }} /></div>
              </div>

              <div className="mt-4">
                <div className="mb-2 text-[11px] font-mono uppercase tracking-wide text-slate-500">Active associates & assignments</div>
                <div className="space-y-2">
                  {members.map((m) => (
                    <button key={m.id} onClick={() => setPanelId(m.id)} className="flex w-full items-center gap-3 rounded-lg border border-white/6 bg-[#030A16]/40 px-3 py-2 text-left hover:border-[#00E5FF]/30" data-testid={`dept-emp-${m.id}`}>
                      <EmployeeAvatar employee={m} size={34} />
                      <div className="min-w-0 flex-1"><div className="text-sm text-white">{m.name}</div><div className="truncate text-[11px] text-slate-500">{m.assignment}</div></div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <div className="mb-2 text-[11px] font-mono uppercase tracking-wide text-slate-500">Recent results</div>
                <div className="flex flex-wrap gap-2">{d.recentResults.map((r) => <span key={r} className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-slate-300">{r}</span>)}</div>
              </div>

              <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
                <ArrowRightLeft size={13} className="text-[#8A2BE2]" /> Collaborates with {others.map((o) => o.name.split(" ")[0]).join(" & ")}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
